/**
 * Browser-side Claude API calls for Vault/BYOK users.
 * All calls go directly from browser → Anthropic. 1895 servers never see the content.
 *
 * Used for: Greenlight Engine analysis (writing craft, characters, emotions, logic).
 * NOTE: Claude API requires CORS-enabled access. If direct browser calls are blocked,
 * vault users can use a CORS proxy or the analysis falls back to backend.
 */
import { getClaudeApiKey, getSecurityTier } from './vaultService';

const CLAUDE_API_URL = 'https://api.anthropic.com/v1/messages';

/**
 * Check if Claude BYOK is active (vault tier + Claude API key present)
 */
export function isClaudeByokActive() {
  return getSecurityTier() === 'vault' && !!getClaudeApiKey();
}

/**
 * Send a text request to Claude (for Greenlight analysis).
 * Returns the text response.
 */
export async function claudeText(systemPrompt, userPrompt, options = {}) {
  const apiKey = getClaudeApiKey();
  if (!apiKey) throw new Error('No Claude API key configured');

  const body = {
    model: options.model || 'claude-sonnet-4-20250514',
    max_tokens: options.maxTokens || 8192,
    temperature: options.temperature ?? 0.7,
    system: systemPrompt,
    messages: [
      { role: 'user', content: userPrompt },
    ],
  };

  const resp = await fetch(CLAUDE_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error?.message || `Claude API error: ${resp.status}`);
  }

  const data = await resp.json();
  const textParts = (data.content || [])
    .filter((b) => b.type === 'text')
    .map((b) => b.text);

  return textParts.join('\n');
}

/**
 * Send a request and parse JSON response.
 */
export async function claudeJson(systemPrompt, userPrompt, options = {}) {
  const raw = await claudeText(systemPrompt, userPrompt, options);

  // Strip markdown code fences
  let text = raw.trim();
  if (text.startsWith('```')) {
    text = text.split('```')[1];
    if (text.startsWith('json')) text = text.substring(4);
  }
  if (text.endsWith('```')) text = text.slice(0, -3);
  text = text.trim();

  return JSON.parse(text);
}
